import { useQuery } from "@tanstack/react-query";
import Header from "@/components/layout/header";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  RefreshCw,
  Activity,
  Clock,
  AlertTriangle,
  Cpu,
  HardDrive,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { ViewGuard } from "@/components/auth/PermissionGuards";
import { MonitoringLayout } from "@/pages/monitoring/layout";

interface RunningQuery {
  sessionId: number;
  duration: string;
  status: string;
  command: string;
  database: string;
  blockedBy: number;
  waitType: string | null;
  cpuMs: number;
  reads: number;
  writes: number;
  currentStatement: string;
  loginName: string;
  hostName: string;
  programName: string;
}

interface SqlActivityResponse {
  timestamp: string;
  database: string;
  runningQueries: RunningQuery[];
  summary: {
    totalQueries: number;
    blockedQueries: number;
    longRunningQueries: number;
  };
}

const fetchSqlActivity = async (): Promise<SqlActivityResponse> => {
  const response = await apiRequest('GET', '/api/sql-activity');
  if (!response.ok) {
    throw new Error('Failed to fetch SQL activity');
  }
  return response.json();
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'running':
      return 'bg-green-100 text-green-800';
    case 'suspended':
      return 'bg-yellow-100 text-yellow-800';
    case 'sleeping':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-blue-100 text-blue-800';
  }
};

const getWaitTypeColor = (waitType: string | null): string => {
  if (!waitType) return '';
  if (waitType.includes('LOG_RATE')) return 'text-orange-600';
  if (waitType.includes('LOCK') || waitType.includes('LCK')) return 'text-red-600';
  if (waitType.includes('IO') || waitType.includes('PAGEIO')) return 'text-blue-600';
  return 'text-gray-600';
};

export default function SqlActivity() {
  const { toast } = useToast();

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['sqlActivity'],
    queryFn: fetchSqlActivity,
    enabled: false, // Manual refresh only
    staleTime: 0,
  });

  const handleRefresh = () => {
    refetch();
    toast({
      title: 'Refreshing',
      description: 'Fetching current SQL activity...',
    });
  };

  return (
    <ViewGuard section="scheduler" fallback={
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-8">
          <div className="mb-4">
            <span className="material-icons text-6xl text-gray-400">block</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to view SQL Activity</p>
        </div>
      </div>
    }>
    <MonitoringLayout>
    <div className="flex flex-col h-full">
      <Header title="SQL Activity Monitor" />
      <main className="flex-1 overflow-y-auto bg-[#F5F5F5] p-6">
        <div className="w-full space-y-6">
          {/* Control Panel */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Running Queries
                </CardTitle>
                <Button onClick={handleRefresh} disabled={isFetching}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Manual refresh only to minimize database load. Click Refresh to see current activity.
              </p>
            </CardHeader>
            <CardContent>
              {/* Summary Cards */}
              {data && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {data.summary.totalQueries}
                      </div>
                      <div className="text-sm text-blue-700">Running Queries</div>
                    </CardContent>
                  </Card>
                  <Card className={`${data.summary.longRunningQueries > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
                    <CardContent className="p-4 text-center">
                      <div className={`text-2xl font-bold ${data.summary.longRunningQueries > 0 ? 'text-yellow-600' : 'text-gray-600'}`}>
                        {data.summary.longRunningQueries}
                      </div>
                      <div className={`text-sm ${data.summary.longRunningQueries > 0 ? 'text-yellow-700' : 'text-gray-700'}`}>
                        Long Running (5m+)
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={`${data.summary.blockedQueries > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                    <CardContent className="p-4 text-center">
                      <div className={`text-2xl font-bold ${data.summary.blockedQueries > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                        {data.summary.blockedQueries}
                      </div>
                      <div className={`text-sm ${data.summary.blockedQueries > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                        Blocked
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-50 border-gray-200">
                    <CardContent className="p-4 text-center">
                      <div className="text-xs text-gray-500">Last Updated</div>
                      <div className="text-sm font-medium text-gray-700">
                        {new Date(data.timestamp).toLocaleTimeString()}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Initial State */}
              {!data && !isLoading && !isError && (
                <div className="text-center py-12 text-gray-500">
                  <Activity className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg mb-2">Click Refresh to load SQL activity</p>
                  <p className="text-sm">This page uses manual refresh to minimize database load</p>
                </div>
              )}

              {/* Loading State */}
              {isLoading && (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              )}

              {/* Error State */}
              {isError && (
                <div className="text-center py-8 text-red-500">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
                  <p>Failed to fetch SQL activity. Please try again.</p>
                </div>
              )}

              {/* Results Table */}
              {data && data.runningQueries.length > 0 && (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">SID</TableHead>
                        <TableHead className="w-[100px]">Duration</TableHead>
                        <TableHead className="w-[80px]">Status</TableHead>
                        <TableHead className="w-[80px]">Command</TableHead>
                        <TableHead className="w-[120px]">Wait Type</TableHead>
                        <TableHead className="w-[80px]">
                          <div className="flex items-center gap-1">
                            <Cpu className="h-3 w-3" />
                            CPU
                          </div>
                        </TableHead>
                        <TableHead className="w-[80px]">
                          <div className="flex items-center gap-1">
                            <HardDrive className="h-3 w-3" />
                            Reads
                          </div>
                        </TableHead>
                        <TableHead className="w-[80px]">Writes</TableHead>
                        <TableHead>Current Statement</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.runningQueries.map((query) => (
                        <TableRow key={query.sessionId} className={query.blockedBy > 0 ? 'bg-red-50' : ''}>
                          <TableCell className="font-mono text-sm">
                            {query.sessionId}
                            {query.blockedBy > 0 && (
                              <div className="text-xs text-red-600">
                                blocked by {query.blockedBy}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3 text-gray-400" />
                              <span className="font-mono text-sm">{query.duration}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(query.status)}>
                              {query.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {query.command}
                          </TableCell>
                          <TableCell>
                            {query.waitType && (
                              <span className={`font-mono text-xs ${getWaitTypeColor(query.waitType)}`}>
                                {query.waitType}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {formatNumber(query.cpuMs)}ms
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {formatNumber(query.reads)}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {formatNumber(query.writes)}
                          </TableCell>
                          <TableCell>
                            <div className="max-w-md truncate font-mono text-xs text-gray-600" title={query.currentStatement}>
                              {query.currentStatement || '-'}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Empty State */}
              {data && data.runningQueries.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="h-12 w-12 mx-auto mb-4 text-green-400" />
                  <p className="text-lg">No active queries</p>
                  <p className="text-sm">The database is idle</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
    </MonitoringLayout>
    </ViewGuard>
  );
}
